import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';

import { useNextInfiniteRequest } from '@/renderer/utils/api/infiniteStore';

import { ResultsTableContent } from './ResultsTable';

// Pending plays that belong in this section — everything ungraded EXCEPT futures
// (futures would bog it down; they're excluded here per product).
const NON_FUTURE_PLAY_TYPES = ['side', 'total', 'prop', 'prop-group'] as const;

// Show the member's whole current pending slate at once (it's small); no paging.
const PENDING_LIMIT = 100;

/**
 * The signed-in member's PENDING (ungraded, non-future) plays, pinned above the
 * graded results so current action is front-and-center. Shows an explicit empty
 * state so a fresh subscriber knows they haven't missed any picks. Reuses the
 * real results row rendering (full detail — the member paid for these).
 *
 * Only renders on the member's own view (userId === 'me').
 */
export function MemberPendingSection({
  userId,
  sportId,
  serviceIds,
}: {
  userId: API.Id | 'me' | 'archive';
  sportId?: number | number[];
  serviceIds?: number[];
}) {
  const request = useNextInfiniteRequest<
    API.MessageReceipt,
    void,
    API.WithPageInput<API.ExtractNestedSortable<API.MessageReceipt>>
  >(
    useMemo(
      () => ({
        key: 'fetchPendingReceipts',
        req: `/api/v1/messageReceipt/${userId}`,
        query: {
          order: { by: 'gameTime' },
          page: { limit: PENDING_LIMIT, offset: 0 },
          data: {
            type: 'release',
            result: 'pending',
            playType: [...NON_FUTURE_PLAY_TYPES],
            [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
            'message.serviceIds': serviceIds,
          },
        },
        skipPoll: true,
        pause: userId !== 'me',
      }),
      [userId, sportId, serviceIds],
    ),
  );

  if (userId !== 'me') {
    return null;
  }

  const rows = request.data?.data ?? [];
  const settled = !request.isLoading && !request.isPending;
  const isEmpty = settled && rows.length === 0;

  return (
    <section className="mb-8">
      <h2 className="mb-3 flex items-center gap-2 font-semibold text-gray-900 text-lg dark:text-gray-100">
        Pending plays
        {rows.length > 0 && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800 text-xs dark:bg-amber-400/20 dark:text-amber-300">
            {rows.length}
          </span>
        )}
      </h2>

      {isEmpty ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-zinc-300 px-6 py-10 text-center dark:border-zinc-700">
          <CheckCircleIcon className="h-8 w-8 text-emerald-500" />
          <p className="font-medium text-gray-900 dark:text-gray-100">No pending plays right now</p>
          <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
            There are no plays out at the moment. New plays will appear here the moment
            they&rsquo;re released.
          </p>
        </div>
      ) : (
        <ResultsTableContent userId={userId} infiniteRequest={request} hidePagination />
      )}
    </section>
  );
}
